import { ContextStateSymbol, ContextUpdateSymbol } from '@/components/global/Context.vue';
import { getRefreshToken, setRefreshToken, getAccessToken, setAccessToken, removeAccessToken } from "@/utils/helpers";
import {BACK_URL} from "@/utils/constants";
import {handleSuccess} from "@/utils/helpers";
import {userData} from "@/mixins/userData";
import {requestValidation} from "@/mixins/requestValidation";

export const sendRequests = {
    data() {
        return {
            isLoading: false,
        }
    },
    mixins: [userData, requestValidation],
    inject: {
        context: {from: ContextStateSymbol, default: null},
        updateContext: {from: ContextUpdateSymbol, default: null},
    },
    methods: {
        async fetchData(url, config = {}, isRetry) {
            config.headers = config.headers || {};
            config.headers["Content-Type"] = "application/json";

            if (typeof window !== 'undefined') {
                const accessToken = getAccessToken();
                if (accessToken) {
                    config.headers.Authorization = `Bearer ${accessToken}`;
                }
            }

            try {
                if(url.indexOf('/oauth2/providers') === -1) {
                    this.isLoading = true;
                }
                const res = await fetch(url, config);

                if (res.ok) {
                    return res;
                }

                let error = null;
                const contentType = res.headers.get('content-type');
                if (contentType && contentType.indexOf('application/json') !== -1) {
                    try {
                        error = await res.json();
                    } catch (jsonError) {
                        console.error('Error parsing JSON:', jsonError);
                    }
                }

                if (error) {
                    await this.handleNotOk(res, error);
                }

                if (res.status === 401 && !isRetry && url.indexOf('oauth2/token') === -1) {
                    const remember = localStorage.getItem('cbgIsRemember');
                    await this.getToken({ grantType: 'RefreshToken', refreshToken: getRefreshToken() }, remember, true);
                    await this.fetchData(url, config, true);
                }

                if (res.status === 401 && isRetry) {
                    removeAccessToken();
                    this.logOut();
                }

                return res;
            } catch (err) {
                throw err;
            } finally {
                this.isLoading = false;
            }
        },

        async getToken(payload, remember, isRetry) {
            const response = await this.fetchData(`${BACK_URL}/oauth2/token`,
                {
                    method: 'POST',
                    credentials: 'include',
                    body: JSON.stringify(payload),
                }, isRetry);

            if (response.ok) {
                const token = await response.json();
                setRefreshToken(token.refreshToken);
                setAccessToken(token.accessToken, remember);
            }

            return response;
        },

        async fetchUserData() {
            try {
                const response = await this.fetchData(`${BACK_URL}/account`);
                const data = await response.json();
                this.updateContext && await this.updateContext('userData', data);
                this.oldUserData && (this.oldUserData = { ...data });
                await handleSuccess('cbg_auth_update', { ...data });
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        },

        async verifyUserRequest(code) {
            try {
                const response = await this.fetchData(`${BACK_URL}/account/verify`, {
                    method: 'POST',
                    body: JSON.stringify({ code }),
                });

                if (response.status === 400) {
                    this.updateContext && this.updateContext('step', 5);
                }

                if (response && response.ok) {
                    this.updateContext && this.updateContext('step', 6);
                    this.updateContext && this.updateContext('isOpen', true);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        },

        async registerUserRequest() {
            try {
                const response = await this.fetchData(`${BACK_URL}/account/register`, {
                    method: 'POST',
                    body: JSON.stringify({
                        email: this.email,
                        password: this.password,
                        login: this.login,
                    }),
                });

                if (response.status === 400) {
                    this.updateContext && this.updateContext('isSaved', true);
                    this.isLoginWrong && this.updateContext && this.updateContext('errorMessage', 'This login is already taken');
                    this.isEmailWrong.taken && this.updateContext && this.updateContext('errorMessage', 'This email is already taken');
                }

                if (response && response.ok) {
                    this.updateContext && this.updateContext('step', 2);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        },

        async loginUserRequest() {
            const isRemember = localStorage.getItem('cbgIsRemember');

            try {
                const response = await this.getToken({
                    grantType: 'Password',
                    username: this.email,
                    password: this.password,
                }, isRemember );

                if (response.status === 400) {
                    this.updateContext && this.updateContext('isSaved', true);
                    this.updateContext && this.updateContext('errorMessage', 'Invalid email or password');
                }

                if (response && response.ok) {
                    await this.fetchUserData();
                    await handleSuccess('cbg_auth_login', {...this.context.userData, isRemember});
                    this.updateContext && this.updateContext('isAuth', true);
                    this.updateContext && this.updateContext('isOpen', false);
                    this.updateContext && this.updateContext('isProviderAuth', false);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        },

        async loginViaProviderRequest(code, provider) {
            const isRemember = localStorage.getItem('cbgIsRemember');

            try {
                const response = await this.getToken({
                    grantType: 'AuthorizationCode',
                    code,
                    provider
                }, true );

                if (response.status === 400) {
                    this.updateContext && this.updateContext('isSaved', true);
                    this.updateContext && this.updateContext('errorMessage', 'Something went wrong');
                }

                if (response && response.ok) {
                    await this.fetchUserData();
                    await handleSuccess('cbg_auth_login', {...this.context.userData, isRemember});
                    this.updateContext && this.updateContext('isAuth', true);
                    this.updateContext && this.updateContext('isOpen', false);
                    this.updateContext && this.updateContext('isProviderAuth', true);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        },

        async changeUserRequest(newData) {
            this.checkDataValues(newData);
            try {
                const response = await this.fetchData(`${BACK_URL}/account`, {
                    method: 'PUT',
                    body: JSON.stringify(newData),
                });

                if (response && response.ok) {
                    await this.fetchUserData();
                    this.updateContext && this.updateContext('step', 0);
                    this.updateContext && this.updateContext('isOpen', false);
                    this.updateContext && this.updateContext('isSaved', true);
                }

            } catch (error) {
                console.error('Error:', error);
            }
        },

        async changePasswordRequest(oldPassword, newPassword) {
            try {
                const response = await this.fetchData(`${BACK_URL}/account/password`, {
                    method: 'POST',
                    body: JSON.stringify({
                        oldPassword,
                        newPassword,
                    }),
                });

                if (response && response.ok) {
                    await this.fetchUserData();
                    this.updateContext && this.updateContext('step', 1);
                    this.updateContext && this.updateContext('isSaved', true);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        },

        async resetPasswordRequest(email) {
            try {
                const response = await this.fetchData(`${BACK_URL}/account/reset-password`, {
                    method: 'POST',
                    body: JSON.stringify({ email }),
                });

                if (response && response.ok) {
                    this.updateContext && this.updateContext('step', 2);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        },

        async newPasswordRequest() {
            const code = this.context.changeToken;
            if (!code) {
                await this.updateContext && this.updateContext('step', 5);
                return;
            }

            try {
                const response = await this.fetchData(`${BACK_URL}/account/set-new-password`, {
                    method: 'POST',
                    body: JSON.stringify({
                        code,
                        password: this.password,
                    }),
                });
                
                if (response.status === 400) {
                    this.updateContext && this.updateContext('step', 5);
                }

                if (response && response.ok) {
                    await this.updateContext && this.updateContext('step', 0);
                    await this.updateContext && this.updateContext('changeToken', null);
                    await this.updateContext && this.updateContext('isSaved', true);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        },

        async getProviders() {
            const providers = await this.fetchData(`${BACK_URL}/oauth2/providers`);
            const data = await providers.json();
            return data;
        },


        async handleNotOk(response, data) {
            if (response.status !== 400) {
                await this.updateContext && this.updateContext('step', 5);
                this.updateContext && this.updateContext('isOpen', true);
            } else {
                data && data.errors && this.handleValidationErrors(data.errors);
            }
        },

        checkDataValues(data) {
            for (const key in data) {
                if (data.hasOwnProperty(key) && data[key] === "") {
                    data[key] = null;
                }
            }
        },

        handleValidationErrors(errors) {
            const isEmailWrong = errors.some(err => err.message === 'Invalid email address' || err.message === 'User not found');
            const isEmailTaken = errors.some(err => err.message === 'Email already in use');
            const isEmailNotVerified = errors.some(err => err.message === 'User is not verified');
            const isNickWrong = errors.some(err => err.message === 'Nickname is required');
            const isNickTaken = errors.some(err => err.message === 'Nickname already in use');
            const isLoginTaken = errors.some(err => err.message === 'Login already in use');
            const isPasswordWrong = errors.some(err => err.path === 'User.Password' || err.message === 'Incorrect password' || err.message === 'Invalid old password');
            const isNewPasswordSame = errors.some(err => err.message === 'New password cannot be the same as the old password');

            this.isEmailWrong.wrong = isEmailWrong;
            this.isEmailWrong.taken = isEmailTaken;
            this.isEmailWrong.notVerified = isEmailNotVerified;
            this.isNickWrong.wrong = isNickWrong;
            this.isNickWrong.taken = isNickTaken;
            this.isLoginWrong = isLoginTaken;

            if (this.isPasswordWrong) {
                this.isPasswordWrong = false;
            }
            setTimeout(() => {
                this.isPasswordWrong = isPasswordWrong;
            }, 0);

            if (this.isNewPasswordSame) {
                this.isNewPasswordSame = false;
            }
            setTimeout(() => {
                this.isNewPasswordSame = isNewPasswordSame;
            }, 0);
        }
    }
}